import { Routes } from '@angular/router';
import { authGuard } from './auth/guards/auth.guard';

/**
 * Every authenticated screen. Grouped under one guarded parent so the guard is
 * declared once rather than repeated on each entry; the URLs are unchanged
 * because the parent has an empty path.
 */
const protectedRoutes: Routes = [
  {
    path: 'alterar-senha',
    loadComponent: () => import('./auth/pages/change-password/change-password').then(m => m.ChangePassword)
  },
  {
    path: 'dashboard',
    loadComponent: () => import('./features/dashboard/pages/dashboard/dashboard').then(m => m.DashboardComponent)
  },
  {
    path: 'clientes',
    loadComponent: () => import('./features/customers/pages/customer-list/customer-list').then(m => m.CustomerList)
  },
  {
    path: 'clientes/novo',
    loadComponent: () => import('./features/customers/pages/customer-form/customer-form').then(m => m.CustomerForm)
  },
  {
    path: 'clientes/:id',
    loadComponent: () => import('./features/customers/pages/customer-form/customer-form').then(m => m.CustomerForm)
  },
  {
    path: 'fornecedores',
    loadComponent: () => import('./features/suppliers/pages/supplier-list/supplier-list').then(m => m.SupplierList)
  },
  {
    path: 'fornecedores/novo',
    loadComponent: () => import('./features/suppliers/pages/supplier-form/supplier-form').then(m => m.SupplierForm)
  },
  {
    path: 'fornecedores/:id',
    loadComponent: () => import('./features/suppliers/pages/supplier-form/supplier-form').then(m => m.SupplierForm)
  },
  {
    path: 'produtos',
    loadComponent: () => import('./features/products/pages/product-list/product-list').then(m => m.ProductList)
  },
  {
    path: 'produtos/novo',
    loadComponent: () => import('./features/products/pages/product-form/product-form').then(m => m.ProductForm)
  },
  {
    path: 'produtos/:id',
    loadComponent: () => import('./features/products/pages/product-form/product-form').then(m => m.ProductForm)
  },
  {
    path: 'categorias',
    loadComponent: () => import('./features/categories/pages/category-list/category-list').then(m => m.CategoryList)
  },
  {
    path: 'categorias/nova',
    loadComponent: () => import('./features/categories/pages/category-form/category-form').then(m => m.CategoryForm)
  },
  {
    path: 'categorias/:id',
    loadComponent: () => import('./features/categories/pages/category-form/category-form').then(m => m.CategoryForm)
  },
  {
    path: 'vendas',
    loadComponent: () => import('./features/orders/pages/order-list/order-list').then(m => m.OrderList)
  },
  {
    path: 'vendas/novo',
    loadComponent: () => import('./features/orders/pages/order-create/order-create').then(m => m.OrderCreate)
  },
  {
    path: 'vendas/:id/editar',
    loadComponent: () => import('./features/orders/pages/order-edit/order-edit').then(m => m.OrderEdit)
  },
  {
    path: 'vendas/:id',
    loadComponent: () => import('./features/orders/pages/order-detail/order-detail').then(m => m.OrderDetail)
  },
  {
    path: 'estoque/entradas',
    loadComponent: () => import('./features/stock/pages/stock-entry-list/stock-entry-list').then(m => m.StockEntryList)
  },
  {
    path: 'estoque/entradas/nova',
    loadComponent: () => import('./features/stock/pages/stock-entry-form/stock-entry-form').then(m => m.StockEntryForm)
  },
  {
    path: 'estoque/entradas/:id',
    loadComponent: () => import('./features/stock/pages/stock-entry-detail/stock-entry-detail').then(m => m.StockEntryDetail)
  },
  {
    path: 'estoque/movimentacoes',
    loadComponent: () => import('./features/stock/pages/stock-movement/stock-movement').then(m => m.StockMovement)
  },
  {
    path: 'despesas',
    loadComponent: () => import('./features/expenses/pages/expense-list/expense-list').then(m => m.ExpenseList)
  },
  {
    path: 'despesas/nova',
    loadComponent: () => import('./features/expenses/pages/expense-form/expense-form').then(m => m.ExpenseForm)
  },
  {
    path: 'despesas/tipos',
    loadComponent: () => import('./features/expenses/pages/expense-type-list/expense-type-list').then(m => m.ExpenseTypeList)
  },
  {
    path: 'despesas/:id',
    loadComponent: () => import('./features/expenses/pages/expense-form/expense-form').then(m => m.ExpenseForm)
  },
  {
    path: 'configuracoes',
    loadComponent: () => import('./features/settings/pages/settings/settings').then(m => m.Settings)
  },
  {
    path: 'caixa',
    loadComponent: () => import('./features/cash/pages/cash-page/cash-page').then(m => m.CashPage)
  },
  {
    path: 'caixa/sessao/:id',
    loadComponent: () => import('./features/cash/pages/cash-session-detail/cash-session-detail').then(m => m.CashSessionDetail)
  },
  {
    path: 'caixa/historico',
    loadComponent: () => import('./features/cash/pages/cash-history/cash-history').then(m => m.CashHistory)
  },
  {
    path: 'relatorios/vendas',
    loadComponent: () => import('./features/reports/pages/vendas/vendas').then(m => m.Vendas)
  },
  {
    path: 'relatorios/despesas',
    loadComponent: () => import('./features/reports/pages/despesas/despesas').then(m => m.Despesas)
  },
  {
    path: 'relatorios/vendas-vs-despesas',
    loadComponent: () => import('./features/reports/pages/vendas-vs-despesas/vendas-vs-despesas').then(m => m.VendasVsDespesas)
  },
  {
    path: 'relatorios/fechamento',
    loadComponent: () => import('./features/reports/pages/fechamento/fechamento').then(m => m.Fechamento)
  },
];

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./auth/pages/login/login').then(m => m.Login)
  },
  // Must precede the guarded parent: an empty-path parent with children
  // consumes '' and will not fall through to a later redirect.
  {
    path: '',
    redirectTo: '/dashboard',
    pathMatch: 'full'
  },
  {
    path: '',
    canActivate: [authGuard],
    children: protectedRoutes,
  },
  {
    path: '**',
    redirectTo: '/dashboard'
  }
];
